import { useEffect } from 'react';

// Field selector — what counts as a navigable cell control. Buttons are
// opt-in via `data-grid-field` (e.g. the ItemPicker trigger). Disabled and
// hidden inputs are skipped.
const FIELD_SELECTOR =
  'input:not([type="hidden"]):not([disabled]):not([readonly]), ' +
  'select:not([disabled]), ' +
  'textarea:not([disabled]):not([readonly]), ' +
  'button[data-grid-field]:not([disabled])';

// Sub-trees that opt out of grid nav — any focusable inside one (popovers,
// dropdowns inside a cell) keeps its native arrow-key behavior.
const SKIP_ATTR = 'data-grid-nav-skip';

// Marker attribute set on a field that was reached via arrow navigation.
// While present, typing is blocked and a visual outline is shown (via CSS
// in index.css). Pressing F2 (or clicking into the field) clears it, which
// puts the user into edit mode.
const NAV_MODE_ATTR = 'data-grid-nav-mode';

function setNavMode(el: HTMLElement) { el.setAttribute(NAV_MODE_ATTR, 'true'); }
function clearNavMode(el: HTMLElement | null) { if (el) el.removeAttribute(NAV_MODE_ATTR); }

// Keys that would start editing (type a character, delete one, commit).
// If any of these are pressed while in nav mode, we swallow them so the
// user has to press F2 (or click) first.
function isEditKey(e: KeyboardEvent): boolean {
  if (e.ctrlKey || e.metaKey || e.altKey) return false;
  if (e.key.length === 1) return true; // single printable character
  return e.key === 'Backspace' || e.key === 'Delete' || e.key === 'Enter';
}

// Excel-style cell navigation:
//   - Click or Tab into a cell → edit mode (type immediately).
//   - Arrow keys from one cell to the next → navigate, landing in "select"
//     mode. The field has focus but typing is blocked until the user hits
//     F2 (or clicks the field). F2 also selects the current value so the
//     next keystroke overwrites.
//   - ← / → only navigate between cells when a text input's caret is at
//     the edge of its value, so the cursor can still move inside the
//     current field during edit mode.
export function useTableArrowNav() {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null;
      if (!t) return;

      // F2 toggles out of nav mode on the focused field. For buttons with
      // data-grid-field (the ItemPicker trigger) it also activates the
      // button so the picker opens.
      if (e.key === 'F2') {
        if (t.hasAttribute(NAV_MODE_ATTR)) {
          e.preventDefault();
          clearNavMode(t);
          if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement) {
            try { t.select(); } catch { /* date/time inputs throw — harmless */ }
          } else if (t instanceof HTMLButtonElement) {
            t.click();
          }
        }
        return;
      }

      // While in nav mode, pressing any edit key (printable char, Backspace,
      // Delete, Enter) also enters edit mode — not just F2. To make typing
      // *replace* the existing cell value (Excel behaviour), we select the
      // whole value before letting the key through; the browser then
      // overwrites the selection on the next input event. Enter is the
      // exception: we eat it so it doesn't accidentally submit a parent
      // form, but we still exit nav mode so the user can start typing.
      if (t.hasAttribute(NAV_MODE_ATTR) && isEditKey(e)) {
        clearNavMode(t);
        if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement) {
          try { t.select(); } catch { /* date/time inputs throw — harmless */ }
        }
        if (e.key === 'Enter') e.preventDefault();
        return;
      }

      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown'
        && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

      // Opt-out: any ancestor marked with data-grid-nav-skip keeps its
      // native arrow behaviour (e.g. ItemPicker's search list).
      if (t.closest(`[${SKIP_ATTR}]`)) return;
      if (!t.matches(FIELD_SELECTOR)) return;
      const td = t.closest('td');
      const tr = td?.parentElement as HTMLTableRowElement | null;
      const tbody = tr?.parentElement as HTMLElement | null;
      if (!td || !tr || !tbody || tbody.tagName !== 'TBODY') return;

      // For text-style inputs, only horizontal-jump at caret edges so the
      // user can still move the cursor within a value they're editing.
      // In nav mode we don't care — no caret to respect, always navigate.
      if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && !t.hasAttribute(NAV_MODE_ATTR)) {
        if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement) {
          const tag = (t instanceof HTMLInputElement ? (t.type || 'text') : 'text').toLowerCase();
          const isText = ['text', 'email', 'url', 'search', 'tel', 'password', 'number', ''].includes(tag);
          if (isText) {
            const start = t.selectionStart;
            const end = t.selectionEnd;
            const len = (t.value ?? '').length;
            if (start !== end) return;
            if (e.key === 'ArrowLeft' && (start ?? 0) > 0) return;
            if (e.key === 'ArrowRight' && (start ?? 0) < len) return;
          }
        }
      }

      const rows = Array.from(tbody.children).filter((c) => c.tagName === 'TR') as HTMLTableRowElement[];
      const rIdx = rows.indexOf(tr);
      const cells = Array.from(tr.children).filter((c) => c.tagName === 'TD') as HTMLTableCellElement[];
      const cIdx = cells.indexOf(td as HTMLTableCellElement);

      function findInCell(cell: HTMLTableCellElement | undefined): HTMLElement | null {
        return (cell?.querySelector(FIELD_SELECTOR) as HTMLElement | null) || null;
      }
      function findInRow(row: HTMLTableRowElement | undefined, colHint: number): HTMLElement | null {
        if (!row) return null;
        const rc = Array.from(row.children).filter((c) => c.tagName === 'TD') as HTMLTableCellElement[];
        const found = findInCell(rc[colHint]);
        if (found) return found;
        for (let k = 1; k < rc.length; k++) {
          const left = colHint - k >= 0 ? findInCell(rc[colHint - k]) : null;
          if (left) return left;
          const right = colHint + k < rc.length ? findInCell(rc[colHint + k]) : null;
          if (right) return right;
        }
        return null;
      }

      let next: HTMLElement | null = null;
      let crossedBoundary = false;
      if (e.key === 'ArrowDown') next = findInRow(rows[rIdx + 1], cIdx);
      else if (e.key === 'ArrowUp') next = findInRow(rows[rIdx - 1], cIdx);
      else if (e.key === 'ArrowLeft') {
        for (let c = cIdx - 1; c >= 0; c--) {
          const f = findInCell(cells[c]);
          if (f) { next = f; break; }
        }
      } else if (e.key === 'ArrowRight') {
        for (let c = cIdx + 1; c < cells.length; c++) {
          const f = findInCell(cells[c]);
          if (f) { next = f; break; }
        }
      }

      // At the top/bottom boundary of the table, keep walking to the nearest
      // editable field OUTSIDE the tbody — so ↑ from the first line jumps
      // to the last invoice-header field, ↓ from the last line jumps to the
      // memo/save area. We look up every focusable field in document order
      // and pick the one just before/after the current input.
      if (!next && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        const all = Array.from(document.querySelectorAll<HTMLElement>(FIELD_SELECTOR))
          .filter((el) => !el.closest(`[${SKIP_ATTR}]`));
        const idx = all.indexOf(t);
        if (idx >= 0) {
          next = e.key === 'ArrowDown' ? all[idx + 1] || null : all[idx - 1] || null;
          if (next) crossedBoundary = true;
        }
      }

      if (!next) return;
      e.preventDefault();
      clearNavMode(t);         // leaving the previous cell
      // Fields outside a tbody behave normally (Tab flow, edit straight
      // away) — only within-table moves land in "select" mode. So when we
      // leave the table, focus + select the value for immediate typing.
      if (crossedBoundary) {
        next.focus();
        if (next instanceof HTMLInputElement || next instanceof HTMLTextAreaElement) {
          try { next.select(); } catch { /* noop */ }
        }
      } else {
        setNavMode(next);      // entering the next in select-not-edit mode
        next.focus();
      }
      // NOTE: we intentionally do NOT select the input value here — that
      // would drop the user straight into "overwrite on next keystroke"
      // mode, defeating the F2-to-edit convention.
    }

    // When focus leaves a field for any reason, forget its nav-mode flag.
    // This also covers the case where the user clicks on another field
    // (which should start in edit mode, not nav mode).
    function onFocusOut(e: FocusEvent) {
      clearNavMode(e.target as HTMLElement | null);
    }
    // Extra safety: if focus arrives via mouse click (not arrows), strip
    // nav-mode so clicking always means "edit this cell now".
    function onMouseDown(e: MouseEvent) {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const field = (t.closest(FIELD_SELECTOR) as HTMLElement | null);
      if (field) clearNavMode(field);
    }

    window.addEventListener('keydown', onKey, true);
    document.addEventListener('focusout', onFocusOut, true);
    document.addEventListener('mousedown', onMouseDown, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.removeEventListener('focusout', onFocusOut, true);
      document.removeEventListener('mousedown', onMouseDown, true);
    };
  }, []);
}
